import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Button, Card, CardBody, Container, Form, FormGroup, Label } from 'reactstrap';
import { InputField } from 'src/components';
import BetterBreadcrumb from 'src/components/Common/BetterBreadCrumb';
import { useApproveProvisionCertificateMutation, useGetMidTermCandidatesListQuery } from 'src/rtk/features/midTerm/midTermApi';
import loader from '../../assets/images/loader.gif';
import { useLazyGetMidTermEligibleTraineeListExcelQuery } from 'src/rtk/features/exportExcel/exportExcel';

const MidTermCandidatesList = () => {

    let referenceUrl = new URLSearchParams(location.search).get('ref');
    const [sessionName, setSessionName] = useState("");
    const [sessionYear, setSessionYear] = useState("");
    const [approvingRegistrationNo, setApprovingRegistrationNo] = useState('');
    const [approveProvisionCertificate] = useApproveProvisionCertificateMutation();

    const [getMidTermEligibleTraineeListExcel, { isLoading: isExcelLoading, isFetching: isExcelFetching }] = useLazyGetMidTermEligibleTraineeListExcelQuery();

    useEffect(() => {
        const today = new Date();
        const month = today.getMonth() + 1; // getMonth() returns 0–11
        const year = today.getFullYear();

        if (month >= 7 && month <= 12) {
            // July - December
            setSessionName("July-December");
            setSessionYear(year.toString());
        } else {
            // January - June
            setSessionName("January-June");
            setSessionYear((year - 1).toString());
        }
    }, []);

    const sessionList: SelectOption[] =
        [
            { value: 'January-June', label: 'January-June' },
            { value: 'July-December', label: 'July-December' },
        ];

    const year: SelectOption[] = Array.from(
        { length: new Date().getFullYear() - 2025 + 1 },
        (_, index) => {
            const value = (2025 + index).toString();
            return { value, label: value };
        }
    );


    const [page, setPage] = useState<any>(1);
    const [limit, setLimit] = useState<any>(20);
    const [search, setSearch] = useState('');

    const generateQueryUrl = () => {
        return `page=${page}&paginate_per_page=${limit}${search}&session_name=${sessionName}&session_year=${sessionYear}`;
    };

    const handlePageChange = (page: number) => {
        setPage(page);
    };

    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setLimit(newPerPage);
        setPage(page);
    };

    const {
        data: approvalList,
        isSuccess,
        isLoading,
        isFetching,
        refetch
    } = useGetMidTermCandidatesListQuery(generateQueryUrl());


    useEffect(() => {
        refetch();
    }, [referenceUrl]);

    interface SearchFields {
        search?: string;
        session_name?: string;
        session_year?: string;
        bmdc_registration_no?: string;
    }

    const initialValues: SearchFields = {
        search: '',
        session_name: '',
        session_year: '',
        bmdc_registration_no: '',
    };

    const {
        handleSubmit,
        getFieldProps,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        resetForm,
        setFieldValue,
        setErrors,
    } = useFormik({
        initialValues,

        onSubmit: (values: any) => {
            let s: string = '';

            if (values.search !== '') {
                s = s + `&search=${values.search}`;
            }

            // if (values.session_name !== '') {
            //     s = s + `&session_name=${values.session_name}`;
            // }

            // if (values.session_year !== '') {
            //     s = s + `&session_year=${values.session_year}`;
            // }
            if (values.bmdc_registration_no !== '') {
                s = s + `&bmdc_registration_no=${values.bmdc_registration_no}`;
            }

            setSearch(s);
            setPage('1');
            console.log(values);
        },
    });

    const columns: TableColumn<any>[] = [
        {
            name: <div>Sl. No.</div>,
            cell: (row, index) => <strong>{((page - 1) * limit + index) + 1}</strong>,
            width: '80px',
        },

        {
            name: <div>Name</div>,
            selector: (row) => row?.personal_info?.full_name,
            sortable: false,
            wrap: true,
        },
        {
            name: <div>BMDC Reg. No</div>,
            selector: (row) => row?.bmdc_registration_no,
            sortable: false,
        },
        {
            name: <div>Subject</div>,
            selector: (row) => row?.subject_name,
            sortable: false,
        },
        {
            name: <div>Status</div>,
            cell: (row) => {
                const isApproved =
                    String(row?.provision_certificate_status || '').toLowerCase() === 'approve';

                return (
                    <span className={`badge rounded-pill ${isApproved ? 'bg-success' : 'bg-warning text-dark'}`}>
                        {isApproved ? 'Approve' : 'Pending'}
                    </span>
                );
            },
            sortable: false,
        },
        {
            name: <div>Action</div>,
            cell: (row) => (
                <Button
                    type="button"
                    color="success"
                    size="sm"
                    disabled={
                        !row?.registration_no ||
                        Boolean(approvingRegistrationNo) ||
                        String(row?.provision_certificate_status || '').toLowerCase() === 'approve'
                    }
                    onClick={() => handleProvisionCertificateApproval(row?.registration_no)}
                >
                    {approvingRegistrationNo === row?.registration_no
                        ? 'Approving...'
                        : String(row?.provision_certificate_status || '').toLowerCase() === 'approve'
                            ? 'Approved'
                            : 'Approve'}
                </Button>
            ),
            ignoreRowClick: true,
            button: true,
        },
    ];

    const handleProvisionCertificateApproval = async (registrationNo?: string) => {
        if (!registrationNo) {
            toast.error('Registration number is unavailable.');
            return;
        }

        setApprovingRegistrationNo(registrationNo);
        try {
            const response = await approveProvisionCertificate(registrationNo).unwrap();
            toast.success(response?.message || 'Provisional certificate approved successfully.');
        } catch (error: any) {
            toast.error(error?.data?.message || error?.message || 'Unable to approve provisional certificate.');
        } finally {
            setApprovingRegistrationNo('');
        }
    };

    const handleExport = async () => {
        try {
            const csvText = await getMidTermEligibleTraineeListExcel(`session_name=${sessionName}&session_year=${sessionYear}`).unwrap();
            const blob = new Blob([csvText], {
                type: 'text/csv;charset=utf-8;',
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');

            a.href = url;
            a.download = `Midterm_Eligible_Applicant_List_${Date.now()}.csv`;
            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed', err);
        }
    };

    return (
        <div className="page-content">
            {isLoading || isFetching || isExcelLoading || isExcelFetching ? (
                <div className="overlay">
                    <img src={loader} alt="Loading" height={100} width={300} />
                </div>
            ) : null}
            <Container fluid>
                <BetterBreadcrumb
                    breadcrumbItem={[{ link: '#', name: 'Admin' }]}
                    title={'Mid Term Exam Candidates List'}
                />
                <Card>
                    <CardBody>
                        <Form
                            className="custom-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            onReset={(e) => {
                                e.preventDefault();
                                handleSubmit();
                                resetForm();
                            }}
                        >
                            <div className="filter-wrapper">
                                <FormGroup>
                                    <Label for="bmdc_registration_no">BMDC Reg. No</Label>
                                    <InputField
                                        type="text"
                                        placeholder="BMDC Reg. No"
                                        {...getFieldProps('bmdc_registration_no')}
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label for="session_name">Session</Label>
                                    <InputField
                                        name='session_name'
                                        type="select"
                                        placeholder="Session"
                                        options={sessionList}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                            setSessionName(e.target.value);
                                        }}
                                        value={sessionName}
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label for="session_year">Year</Label>
                                    <InputField
                                        type="select"
                                        placeholder="Year"
                                        options={year}
                                        name='session_year'
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                            setSessionYear(e.target.value);
                                        }}
                                        value={sessionYear}
                                    />
                                </FormGroup>

                                <FormGroup className="btn-column mb-3">
                                    <Button color="warning" type="reset" outline>Reset</Button>
                                    <Button type="submit" color='primary'>Search</Button>
                                </FormGroup>

                                <FormGroup className='d-flex justify-content-end'>
                                    <Button
                                        type="button"
                                        color="success"
                                        outline
                                        className="fw-bold"
                                        onClick={handleExport}
                                    >
                                        <i className="fa-solid fa-file-arrow-down"></i>{' '}
                                        Export CSV
                                    </Button>
                                </FormGroup>
                            </div>
                        </Form>

                        <DataTable
                            columns={columns}
                            data={approvalList?.data?.data!}
                            pagination
                            paginationServer
                            progressPending={isLoading}
                            expandOnRowClicked
                            highlightOnHover
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationTotalRows={approvalList?.data?.total}
                            paginationRowsPerPageOptions={[2, 5, 10, 20, 50, 100, 200]}
                            responsive
                            persistTableHead={true}
                        />
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default MidTermCandidatesList;
